import { useActiveCompany } from '~/hooks/useActiveCompany';
import { useActiveCompanyUser } from '~/hooks/useActiveCompanyUser';
import { FeatureFlag, FeatureScope, LeadSource, UserRole } from '~/types/company';

// Catalog membership: the company has the flag, regardless of scope.
// Replaces the legacy `company.features` membership checks.
const useCompanyHasFeature = (featureFlag: FeatureFlag) => {
  const company = useActiveCompany();
  return (company.featuresV2 ?? []).some((feature) => feature.value === featureFlag);
};

export const useFeatureFlag = (featureFlags: FeatureFlag | FeatureFlag[], whitelistedRoles?: UserRole[]) => {
  const company = useActiveCompany();
  const companyUser = useActiveCompanyUser();
  const requiredFeatures = Array.isArray(featureFlags) ? featureFlags : [featureFlags];

  const isWhitelisted = whitelistedRoles?.includes(company.userRole) ?? false;

  const hasUserFeature = (featureFlag: FeatureFlag) =>
    (companyUser?.featuresV2 ?? []).some(
      (feature) => feature.value === featureFlag && feature.scope === FeatureScope.User,
    );

  return requiredFeatures.every((featureFlag) => {
    const companyFeatures = (company.featuresV2 ?? []).find((feature) => feature.value === featureFlag);
    if (!companyFeatures) return false;

    // whitelisted roles bypass user-level feature checks
    if (isWhitelisted) return true;

    // COMPANY scope applies to everyone; USER scope also requires the company-user assignment
    return companyFeatures.scope === FeatureScope.Company || hasUserFeature(featureFlag);
  });
};

export const useHasInvoicing = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.Invoicing, whitelistedRoles);

export const useHasTreasury = (whitelistedRoles?: UserRole[]) => useFeatureFlag(FeatureFlag.Treasury, whitelistedRoles);

export const useHasSeeInvoices = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.SeeInvoices, whitelistedRoles);

export const useHasPayments = (whitelistedRoles?: UserRole[]) => useFeatureFlag(FeatureFlag.Payments, whitelistedRoles);

export const useHasAutomaticReconciliation = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.AutomaticReconciliation, whitelistedRoles);

export const useHasCashflow = (whitelistedRoles?: UserRole[]) => useFeatureFlag(FeatureFlag.Cashflow, whitelistedRoles);

export const useHasAnalyticsDashboardsPurchases = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.AnalyticsDashboardsPurchases, whitelistedRoles);

export const useHasAnalyticsDashboardsSales = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.AnalyticsDashboardsSales, whitelistedRoles);

export const useHasAnalyticsDashboardsBills = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.AnalyticsDashboardsBills, whitelistedRoles);

export const useHasOutstanding = (whitelistedRoles?: UserRole[]) =>
  useFeatureFlag(FeatureFlag.Outstanding, whitelistedRoles);

export const useHasHideBalances = () => {
  const value = useFeatureFlag(FeatureFlag.SeeBalances);
  const isEntrepeneur = useIsEntrepeneur();

  if (isEntrepeneur) {
    return false;
  }
  return value !== true;
};

export const useIsEntrepeneur = () => {
  const company = useActiveCompany();
  return company.userRole === UserRole.ENTREPRENEUR;
};

export const useIsPayrollConsultant = () => {
  const company = useActiveCompany();
  return company.userRole === UserRole.PAYROLL_CONSULTANT;
};

export const useIsBankAccountLeadSource = () => {
  const company = useActiveCompany();
  return company.leadSource === LeadSource.BANK_ACCOUNT;
};

export const useHasBookkeeping = () => useFeatureFlag(FeatureFlag.Bookkeeping);

export const useHasSplitTransactions = () => useFeatureFlag(FeatureFlag.SplitTransactions);

export const useHasSplitConsent = () => useFeatureFlag(FeatureFlag.SplitConsent);

export const useHasSplitByCostCenter = () => useFeatureFlag(FeatureFlag.SplitByCostCenter);

export const useHasAutomaticCategorizationTransaction = () =>
  useFeatureFlag(FeatureFlag.AutomaticCategorizationTransaction, [UserRole.ENTREPRENEUR]);

export const useHasCashback = () => {
  // Company-level feature: once enabled for the company, all users must inherit access.
  return useCompanyHasFeature(FeatureFlag.SibillCashback);
};

export const useHasAutomaticSecondCompanyOnboarding = () =>
  useFeatureFlag(FeatureFlag.AutomaticOnboardingSecondCompanies);

export const useHasIntegrationApi = () => {
  return useFeatureFlag(FeatureFlag.IntegrationApi);
};

export const useHasAiChat = () => useFeatureFlag(FeatureFlag.AiChat);

export const useHasNewAiVatProposals = () => {
  // Company-level feature: once enabled for the company, all users must inherit access.
  return useCompanyHasFeature(FeatureFlag.NewAiVatProposals);
};

export const useHasPayrollBookkeepingProposals = () => useFeatureFlag(FeatureFlag.PayrollBookkeepingProposals);

export const useHasSubscriptionManagement = () => useFeatureFlag(FeatureFlag.SubscriptionManagement);

export const useHasCreditLines = () => useFeatureFlag(FeatureFlag.CreditLines, [UserRole.ENTREPRENEUR]);

export const useHasF24Management = () => useFeatureFlag(FeatureFlag.F24Management);

export const useHasBilancioCee = () => useFeatureFlag(FeatureFlag.BilancioCee);
